/* ShopHardGuard Smart Search | Shopify Predictive Search API | no dependencies */
(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const textNode = (tag, cls, value) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    el.textContent = value == null ? '' : String(value);
    return el;
  };
  const iconSvg = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('width', '15');
    svg.setAttribute('height', '15');
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '11'); circle.setAttribute('cy', '11'); circle.setAttribute('r', '7');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'm16 16 5 5');
    svg.append(circle, path);
    return svg;
  };

  const localLink = (url, fallback) => {
    if (typeof url !== 'string' || !url.trim()) return fallback;
    try {
      const parsed = new URL(url, location.origin);
      return parsed.origin === location.origin && /^https?:$/.test(parsed.protocol) ? parsed.href : fallback;
    } catch (_) { return fallback; }
  };
  const safeImage = url => {
    if (url && typeof url === 'object') url = url.url || url.src;
    if (typeof url !== 'string' || !url.trim()) return '';
    try {
      const parsed = new URL(url.startsWith('//') ? location.protocol + url : url, location.origin);
      return /^https?:$/.test(parsed.protocol) ? parsed.href : '';
    } catch (_) { return ''; }
  };

  function init(root) {
    if (root.dataset.hgpsReady === 'true') return;
    root.dataset.hgpsReady = 'true';
    const input = $('[data-hgps-input]', root);
    const panel = $('[data-hgps-live]', root);
    const defaults = $('[data-hgps-default]', root);
    const results = $('[data-hgps-options]', root);
    const queriesBox = $('[data-hgps-queries]', root);
    const productsBox = $('[data-hgps-products]', root);
    const collectionsBox = $('[data-hgps-collections]', root);
    const message = $('[data-hgps-message]', root);
    const queryDisplay = $('[data-hgps-query-display]', root);
    const count = $('[data-hgps-result-count]', root);
    const viewAll = $('[data-hgps-view-all]', root);
    const clear = $('[data-hgps-clear]', root);
    if (!input || !panel || !results) return;

    const base = root.dataset.hgpsSearchUrl || '/search';
    const endpoint = root.dataset.hgpsSuggestUrl || '/search/suggest.json';
    const cache = new Map();
    let controller = null;
    let debounceTimer = null;
    let sequence = 0;
    let active = -1;
    let options = [];

    const makeSearchUrl = term => {
      const url = new URL(base, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url.href;
    };
    const updateClear = () => { clear.hidden = !input.value; };
    const resetActive = () => {
      active = -1;
      input.removeAttribute('aria-activedescendant');
      options.forEach(x => x.removeAttribute('aria-selected'));
    };
    const resetResults = () => {
      queriesBox.replaceChildren(); productsBox.replaceChildren(); collectionsBox.replaceChildren();
      options = []; resetActive();
      input.setAttribute('aria-expanded', 'false');
    };
    const showDefault = () => {
      panel.hidden = true;
      defaults.hidden = false;
      resetResults();
      message.textContent = '';
      updateClear();
    };
    const showPanel = term => {
      defaults.hidden = true;
      panel.hidden = false;
      queryDisplay.textContent = '\u201c' + term + '\u201d';
      viewAll.href = makeSearchUrl(term);
      input.setAttribute('aria-expanded', 'true');
    };
    const addGroupTitle = (box, label) => box.appendChild(textNode('div', 'hgps-group-title', label));
    const register = (anchor, href) => {
      anchor.setAttribute('role', 'option');
      anchor.setAttribute('aria-selected', 'false');
      anchor.id = root.id + 'Option' + options.length;
      anchor.href = href;
      options.push(anchor);
      return anchor;
    };
    const renderQuery = (item, wrap) => {
      const term = typeof item === 'string' ? item : (item?.text || item?.query || '');
      if (!term || typeof term !== 'string') return;
      const a = register(textNode('a', 'hgps-query', ''), makeSearchUrl(term));
      a.append(iconSvg(), textNode('span', '', term));
      wrap.append(a);
    };
    const renderProduct = (product, wrap) => {
      if (!product || !product.title) return;
      const href = localLink(product.url, makeSearchUrl(product.title));
      const a = register(textNode('a', 'hgps-result-product', ''), href);
      const src = safeImage(product.featured_image || product.image);
      const thumb = textNode('span', 'hgps-thumb', '');
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 64;
        img.height = 64;
        thumb.append(img);
      } else {
        thumb.classList.add('hgps-thumb--empty');
        thumb.textContent = 'HG';
      }
      const copy = textNode('span', 'hgps-product-text', '');
      copy.append(textNode('span', 'hgps-product-name', product.title));
      const meta = textNode('span', 'hgps-product-meta', '');
      const rawPrice = product.price_min ?? product.price;
      if (rawPrice !== null && rawPrice !== undefined && Number.isFinite(Number(rawPrice))) {
        const currency = root.dataset.hgpsCurrency || 'USD';
        const locale = root.dataset.hgpsLocale || 'en-US';
        try {
          const formatted = new Intl.NumberFormat(locale, {style:'currency', currency}).format(Number(rawPrice));
          meta.append(textNode('span', 'hgps-price', formatted));
        } catch (_) { /* price formatting not essential */ }
      }
      if (product.available === false) meta.append(textNode('span', 'hgps-sold', 'Sold out'));
      copy.append(meta);
      a.append(thumb, copy);
      wrap.append(a);
    };
    const renderCollection = (item, wrap, kind) => {
      if (!item || !item.title) return;
      const href = localLink(item.url, makeSearchUrl(item.title));
      const a = register(textNode('a', 'hgps-collection', ''), href);
      a.append(textNode('span', '', kind === 'page' ? '↗' : '◇'), textNode('span', '', item.title));
      wrap.append(a);
    };
    const selectedNext = delta => {
      if (!options.length) return;
      active = active < 0 ? (delta > 0 ? 0 : options.length - 1) : (active + delta + options.length) % options.length;
      options.forEach((item, i) => item.setAttribute('aria-selected', i === active ? 'true' : 'false'));
      input.setAttribute('aria-activedescendant', options[active].id);
      options[active].scrollIntoView({block:'nearest'});
    };

    async function search(term) {
      const requestIndex = ++sequence;
      controller?.abort();
      resetResults();
      showPanel(term);
      count.textContent = '';
      message.textContent = 'Finding matching guards...';
      if (cache.has(term.toLowerCase())) {
        render(cache.get(term.toLowerCase()), term, requestIndex);
        return;
      }
      controller = new AbortController();
      try {
        const url = new URL(endpoint, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'query,product,collection,page');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const resp = await fetch(url.href, {
          signal: controller.signal,
          headers: {Accept: 'application/json'},
          credentials: 'same-origin'
        });
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        const json = await resp.json();
        if (requestIndex !== sequence || input.value.trim() !== term) return;
        const data = json?.resources?.results || {};
        if (cache.size >= 35) cache.delete(cache.keys().next().value);
        cache.set(term.toLowerCase(), data);
        render(data, term, requestIndex);
      } catch (error) {
        if (error?.name === 'AbortError' || requestIndex !== sequence) return;
        resetResults();
        showPanel(term);
        message.textContent = 'Instant suggestions are unavailable. Use “View all matching products” to search the store.';
        count.textContent = '';
      }
    }

    function render(data, term, requestIndex) {
      if (requestIndex !== sequence || input.value.trim() !== term) return;
      resetResults();
      showPanel(term);
      const queryItems = Array.isArray(data.queries) ? data.queries.slice(0, 5) : [];
      const productItems = Array.isArray(data.products) ? data.products.slice(0, 6) : [];
      const collectionItems = Array.isArray(data.collections) ? data.collections.slice(0, 3) : [];
      const pageItems = Array.isArray(data.pages) ? data.pages.slice(0, 2) : [];
      if (queryItems.length) {
        addGroupTitle(queriesBox, 'Suggested searches');
        const wrap = textNode('div', 'hgps-queries-list', '');
        queryItems.forEach(q => renderQuery(q, wrap));
        queriesBox.append(wrap);
      }
      if (productItems.length) {
        addGroupTitle(productsBox, 'Matching products');
        const wrap = textNode('div', 'hgps-product-list', '');
        productItems.forEach(p => renderProduct(p, wrap));
        productsBox.append(wrap);
      }
      if (collectionItems.length || pageItems.length) {
        addGroupTitle(collectionsBox, 'Collections & helpful links');
        const wrap = textNode('div', 'hgps-collection-list', '');
        collectionItems.forEach(c => renderCollection(c, wrap, 'collection'));
        pageItems.forEach(p => renderCollection(p, wrap, 'page'));
        collectionsBox.append(wrap);
      }
      count.textContent = productItems.length ? productItems.length + ' products' : '';
      message.textContent = options.length ? '' : 'No instant matches. Try a colorway, PSA, Glass, or browse all results.';
      input.setAttribute('aria-expanded', options.length ? 'true' : 'false');
    }

    const onInput = () => {
      updateClear();
      const value = input.value.trim();
      window.clearTimeout(debounceTimer);
      controller?.abort();
      ++sequence;
      if (value.length < 2) {
        showDefault();
        return;
      }
      resetResults();
      showPanel(value);
      message.textContent = 'Finding matching guards...';
      debounceTimer = window.setTimeout(() => search(value), 190);
    };
    input.addEventListener('input', onInput);
    input.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown' && options.length) { event.preventDefault(); selectedNext(1); }
      if (event.key === 'ArrowUp' && options.length) { event.preventDefault(); selectedNext(-1); }
      if (event.key === 'Enter' && active >= 0 && options[active]) {
        event.preventDefault();
        location.assign(options[active].href);
      }
      if (event.key === 'Escape') {
        const dialog = root.closest('dialog');
        if (dialog?.open) dialog.close();
        else if (input.value) { input.value = ''; onInput(); }
      }
    });
    clear.addEventListener('click', () => {
      input.value = '';
      onInput();
      input.focus();
    });
    root.addEventListener('focusin', e => {
      if (e.target === input && input.value.trim().length >= 2 && panel.hidden) onInput();
    });
    updateClear();
    // The results page can arrive prefilled; keep its submitted search results visible
    // until the customer edits or refocuses the query.
  }

  function initAll(scope = document) { $$('[data-hgps]', scope).forEach(init); }
  initAll();
  document.addEventListener('shopify:section:load', e => initAll(e.target));

  document.addEventListener('click', event => {
    const openButton = event.target.closest('[data-hgps-open]');
    if (openButton) {
      const dialog = $('#HgSearchDialog');
      if (!dialog) return;
      event.preventDefault();
      if (!dialog.open) dialog.showModal();
      const input = $('[data-hgps-input]', dialog);
      window.requestAnimationFrame(() => input?.focus());
      return;
    }
    const closeButton = event.target.closest('[data-hgps-close]');
    if (closeButton) closeButton.closest('dialog')?.close();
  });
})();
